import { useState, useRef, useEffect } from 'react'

type Horizon = 'weekly' | 'monthly' | 'quarterly'

interface HorizonOption {
  value: Horizon
  label: string
  description: string
}

const HORIZON_OPTIONS: HorizonOption[] = [
  { value: 'weekly', label: 'Weekly Review', description: 'Last 7 days' },
  { value: 'monthly', label: 'Monthly Review', description: 'Current calendar month' },
  { value: 'quarterly', label: 'Quarterly Review', description: 'Current calendar quarter' },
]

export default function ExportDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close on click-outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [error])

  const handleExport = async (horizon: Horizon) => {
    setIsOpen(false)
    setIsDownloading(true)
    setError(null)

    try {
      const API = import.meta.env.VITE_API_URL || ''
      const resp = await fetch(`${API}/api/export/review-package?horizon=${horizon}`)
      if (!resp.ok) {
        const errData = await resp.json().catch(() => null)
        throw new Error(errData?.error || `Export failed (${resp.status})`)
      }
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Use the filename from Content-Disposition if available, otherwise fallback
      const disposition = resp.headers.get('Content-Disposition')
      const filenameMatch = disposition?.match(/filename="?([^"]+)"?/)
      a.download = filenameMatch?.[1] || `portfolio-review-${horizon}-${new Date().toISOString().split('T')[0]}.zip`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !isDownloading && setIsOpen(!isOpen)}
        disabled={isDownloading}
        className="px-3 py-1.5 text-sm rounded border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
      >
        {isDownloading ? (
          <>
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Exporting...
          </>
        ) : (
          <>📦 Export Review</>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-64 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg z-50">
          {HORIZON_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => handleExport(option.value)}
              className="w-full text-left px-4 py-3 hover:bg-[var(--color-surface-hover)] transition-colors first:rounded-t-lg last:rounded-b-lg"
            >
              <div className="text-sm font-medium text-[var(--color-text-primary)]">
                {option.label}
              </div>
              <div className="text-xs text-[var(--color-text-muted)]">
                {option.description}
              </div>
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="absolute right-0 top-full mt-1 w-64 px-3 py-2 rounded border border-[var(--color-red)]/30 bg-[var(--color-red)]/10 text-xs text-[var(--color-red)] z-50">
          {error}
        </div>
      )}
    </div>
  )
}
